import { 
  Title, 
  Text, 
  Paper, 
  Stack, 
  Card, 
  Group, 
  Button, 
  Divider,
  Grid,
  List,
  ThemeIcon,
  Box,
  Loader,
  Skeleton
} from '@mantine/core';
import { 
  IconArrowLeft, 
  IconEdit, 
  IconLayoutGrid, 
  IconUsers, 
  IconBulb,
  IconRefresh,
  IconPhoto
} from '@tabler/icons-react';
import { useState } from 'react';

import { AnalysisData, FormData, Improvement } from '@/types';
import { generateImprovedPost } from '@/api/analysis';

interface PostImprovementsProps {
  analysisData: AnalysisData;
  formData: FormData;
  onBackToFeedback: () => void;
  onCreateNewTest?: () => void;
  onCreateImprovedPost?: (improvedContent: string) => void;
}

// Map improvement types to icons and colors
const IMPROVEMENT_TYPES = {
  content: { 
    icon: IconEdit, 
    color: 'blue', 
    label: 'Content Improvement'
  },
  format: { 
    icon: IconLayoutGrid, 
    color: 'violet', 
    label: 'Format Improvement'
  },
  engagement: { 
    icon: IconBulb, 
    color: 'yellow', 
    label: 'Engagement Booster'
  },
  audience: { 
    icon: IconUsers, 
    color: 'teal', 
    label: 'Audience Targeting'
  },
  media: {
    icon: IconPhoto,
    color: 'orange',
    label: 'Media Improvement'
  }
};

const PostImprovements = ({ 
  analysisData, 
  formData,
  onBackToFeedback, 
  onCreateNewTest,
  onCreateImprovedPost
}: PostImprovementsProps) => {
  const { improvements } = analysisData;
  const [isGeneratingImprovedPost, setIsGeneratingImprovedPost] = useState(false);
  
  // Check if improvements are still loading
  const isLoading = improvements.length === 1 && improvements[0].id === 'loading-1';
  
  // Group improvements by type (only if not loading)
  const groupedImprovements = isLoading 
    ? {} 
    : improvements.reduce((acc, improvement) => {
        if (!acc[improvement.type]) {
          acc[improvement.type] = [];
        }
        acc[improvement.type].push(improvement);
        return acc;
      }, {} as Record<string, Improvement[]>);
  
  // Generate an improved post content using Gemini
  const handleGenerateImprovedPost = async () => {
    if (isLoading || !onCreateImprovedPost || isGeneratingImprovedPost) return;
    
    try {
      setIsGeneratingImprovedPost(true);
      // Call the API to generate an improved post
      const improvedContent = await generateImprovedPost(formData, improvements);
      onCreateImprovedPost(improvedContent);
    } catch (error) {
      console.error('Error generating improved post:', error);
    } finally {
      setIsGeneratingImprovedPost(false);
    }
  };
  
  return (
    <Stack gap="xl" className='responsive-container'>
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="xl">
          <Group justify="space-between">
            <Button 
              variant="subtle" 
              leftSection={<IconArrowLeft size={16} />}
              onClick={onBackToFeedback}
            >
              Back to Analysis
            </Button>
          </Group>
          
          <Title order={2} ta="center">
            Improvement Suggestions
          </Title>
          
          <Text c="dimmed" ta="center">
            {isLoading 
              ? 'Generating improvement suggestions based on audience feedback...'
              : 'Based on audience feedback, here are suggested improvements for your LinkedIn post.'}
          </Text>
          
          {isLoading ? (
            // Loading state
            <Stack align="center" gap="xl" py="xl">
              <Loader size="lg" />
              <Text>Analyzing post and generating personalized improvement suggestions...</Text>
              <Grid>
                {[1, 2, 3, 4].map(i => (
                  <Grid.Col key={i} span={{ base: 12, md: 6 }}>
                    <Card p="lg" radius="md" withBorder h="100%">
                      <Stack gap="md">
                        <Group>
                          <Skeleton height={30} circle />
                          <Skeleton height={22} width={120} />
                        </Group>
                        <Stack gap="sm">
                          <Skeleton height={12} />
                          <Skeleton height={12} />
                          <Skeleton height={12} width="80%" />
                        </Stack>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
              </Grid>
            </Stack>
          ) : (
            // Actual improvements
            <Grid>
              {Object.entries(groupedImprovements).map(([type, typeImprovements]) => {
                // Add a safeguard for unknown improvement types
                const improvementType = IMPROVEMENT_TYPES[type as keyof typeof IMPROVEMENT_TYPES];
                
                if (!improvementType) {
                  console.warn(`Unknown improvement type: ${type}`);
                  return null; // Skip rendering this improvement group
                }
                
                const { icon: Icon, color, label } = improvementType;
                
                return (
                  <Grid.Col key={type} span={{ base: 12, md: 6 }}>
                    <Card p="lg" radius="md" withBorder h="100%">
                      <Stack gap="md" h="100%">
                        <Group>
                          <ThemeIcon size="lg" radius="md" color={color}>
                            <Icon size={18} />
                          </ThemeIcon>
                          <Title order={4}>{label}</Title>
                        </Group>
                        
                        <Box style={{ flexGrow: 1 }}>
                          <List spacing="md">
                            {typeImprovements.map((improvement) => (
                              <List.Item key={improvement.id}>
                                <Text size="sm">{improvement.suggestion}</Text>
                              </List.Item>
                            ))}
                          </List>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid.Col>
                );
              })}
            </Grid>
          )}
          
          <Card withBorder p="xl" radius="md" bg="blue.0">
            <Stack gap="md">
              <Title order={3} ta="center">Ready to Apply These Improvements?</Title>
              <Text ta="center">
                {isLoading 
                  ? 'We are tailoring improvements specific to your post and audience...'
                  : 'Review these suggestions carefully, then create an improved version of your post to test its effectiveness.'}
              </Text>
              <Group justify="center">
                {onCreateImprovedPost && (
                  <Button 
                    size="lg" 
                    color="blue"
                    leftSection={<IconRefresh size={18} />}
                    onClick={handleGenerateImprovedPost}
                    disabled={isLoading || isGeneratingImprovedPost}
                    loading={isGeneratingImprovedPost}
                  >
                    {isGeneratingImprovedPost 
                      ? 'Generating Improved Post...' 
                      : 'Apply Improvements & Edit'}
                  </Button>
                )}
                <Button 
                  size="lg" 
                  variant={onCreateImprovedPost ? "outline" : "filled"}
                  color="gray"
                  onClick={onCreateNewTest}
                  disabled={isLoading}
                >
                  {isLoading ? 'Please Wait...' : 'Create New Test'}
                </Button>
              </Group>
            </Stack>
          </Card>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default PostImprovements; 